import { useState } from "react";
import { Alert, Box, Chip, FormControl, Skeleton, Stack, Typography as MuiTypography, useTheme } from "@mui/material";
import { FoodsSelectServerPagination, Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useDelayedSearch } from "../../utils/useDelayedSearch";
import { useGetAllTagTypes } from "../../api/client-api/tag/useGetAllTagTypes";
import { useGetAllTagValues } from "../../api/client-api/tag/useGetAllTagValues";
import { TagType, TagValue, TagContext } from "../../types/tag";
import { RemovableTag } from "src/components/AssociatedStructures";
import { FoodsFieldSkeleton, FoodsServerSelectField } from "src/components/EntityForm/FoodsFields";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { resolveLegacyTagColors, resolveTagColors } from "src/components/TagManagement/tagColors";
import type { ComponentAppearance } from "src/components/appearance";

export interface JsonTagSelectorProps {
    entity: {
        id: string | number;
        tags?: Record<string, string[]>;
    };
    entityClass: string;
    context: TagContext;
    disabled?: boolean;
    variant?: "full" | "compact";
    onTagsChange?: (tags: Record<string, string[]>) => void;
    existingTags?: Record<string, string[]>;
    hideTitle?: boolean;
    appearance?: ComponentAppearance;
}

export const JsonTagSelector = ({
    entity,
    entityClass,
    context,
    disabled = false,
    variant = "full",
    onTagsChange,
    existingTags = {},
    hideTitle = false,
    appearance = "legacy",
}: JsonTagSelectorProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isProductUxV2 = appearance === "product-v2";
    const [selectedTagType, setSelectedTagType] = useState<TagType | null>(null);

    // Search for tag types
    const {
        search: tagTypeSearch,
        setSearch: setTagTypeSearch,
        delayedSearch: delayedTagTypeSearch,
    } = useDelayedSearch();

    // Search for tag values
    const {
        search: tagValueSearch,
        setSearch: setTagValueSearch,
        delayedSearch: delayedTagValueSearch,
    } = useDelayedSearch();

    // Always fetch tag types for the context
    const {
        tagTypes = [],
        isLoading: tagTypesLoading,
        error,
    } = useGetAllTagTypes({
        context,
        search: delayedTagTypeSearch || undefined,
        itemsPerPage: 100,
    });

    // Fetch tag values for selected tag type
    const { data: tagValuesResponse } = useGetAllTagValues(
        selectedTagType ?
            {
                itemsPerPage: 1000,
            }
        :   undefined,
    );
    const tagValues = tagValuesResponse?.tagValues || [];
    const valuesForSelectedType = tagValues.filter((tv) => {
        // Filter for tagTypes...
        if (tv.tagType?.id === selectedTagType?.id) {
            return true;
        }
        return false;
    });
    const remainingTagValues = valuesForSelectedType.filter((tv) => {
        // Filter for already used tags;
        const slug = selectedTagType?.slug;
        if (slug === undefined) return true;
        if (existingTags?.[slug]?.includes(tv.slug)) {
            return false;
        }
        return true;
    });

    const allSlugToValues = tagValues.reduce(
        (acc, v) => {
            acc[v.slug] = v;
            return acc;
        },
        {} as Record<string, TagValue>,
    );

    // Current tags from entity
    const currentTags = entity.tags || {};

    // Helper functions for search filtering
    const isTagTypeSearchFound = (value: string) => {
        if (delayedTagTypeSearch === "") return true;
        return value.toLowerCase().includes(delayedTagTypeSearch.toLowerCase());
    };

    const isTagValueSearchFound = (value: string) => {
        if (delayedTagValueSearch === "") return true;
        return value.toLowerCase().includes(delayedTagValueSearch.toLowerCase());
    };

    const handleTagValueSelect = (tagValue: TagValue) => {
        if (!onTagsChange || !selectedTagType) return;

        const typeSlug = selectedTagType.slug;
        const updatedTags = { ...currentTags };

        const tagValueSlug = tagValue.slug;
        // Check if tag already exists
        if (updatedTags[typeSlug] && updatedTags[typeSlug].includes(tagValueSlug)) {
            return; // Already tagged
        }

        // Check if tag type allows multiple values (default to true if undefined)
        const multiple = selectedTagType.multiple ?? true;

        if (!multiple) {
            // Single-valued: Replace existing value
            updatedTags[typeSlug] = [tagValueSlug];
        } else {
            // Multi-valued: Add to existing values
            if (!updatedTags[typeSlug]) {
                updatedTags[typeSlug] = [];
            }
            updatedTags[typeSlug] = [...updatedTags[typeSlug], tagValueSlug];
        }

        onTagsChange(updatedTags);
        setSelectedTagType(null); // Reset selection
    };

    const handleTagRemove = (typeSlug: string, value: string) => {
        if (!onTagsChange) return;

        const updatedTags = { ...currentTags };
        if (updatedTags[typeSlug]) {
            updatedTags[typeSlug] = updatedTags[typeSlug].filter((v) => v !== value);
            if (updatedTags[typeSlug].length === 0) {
                delete updatedTags[typeSlug];
            }
        }

        onTagsChange(updatedTags);
    };

    const getTagsByType = (typeSlug: string) => {
        return currentTags[typeSlug] || [];
    };

    const getTagTypeBySlug = (slug: string) => {
        return tagTypes.find((tt) => tt.slug === slug);
    };

    if (error) {
        const message = t("tags.messages.failed_to_load_tags", "Failed to load tags. Please try again.");

        return isProductUxV2 ?
                <RemoteErrorAlert message={message} status="server-error" />
            :   <Alert severity="error">{message}</Alert>;
    }

    if (variant === "compact") {
        if (!isProductUxV2) {
            return (
                <Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {Object.entries(currentTags).map(([typeSlug, values]) => {
                            const tagType = getTagTypeBySlug(typeSlug);

                            return values.map((value) => {
                                const colors = resolveLegacyTagColors(tagType);

                                return (
                                    <Chip
                                        key={`${typeSlug}-${value}`}
                                        label={value}
                                        size="small"
                                        sx={{
                                            backgroundColor: colors.background,
                                            color: colors.foreground,
                                            fontWeight: "bold",
                                            fontSize: "0.75rem",
                                            "& .MuiChip-deleteIcon": {
                                                color: colors.foreground,
                                                "&:hover": {
                                                    color: colors.foreground,
                                                    opacity: 0.7,
                                                },
                                            },
                                        }}
                                        onDelete={disabled ? undefined : () => handleTagRemove(typeSlug, value)}
                                    />
                                );
                            });
                        })}
                    </Stack>
                </Box>
            );
        }

        return (
            <Box>
                <Stack direction="row" gap={theme.spacing(1)} flexWrap="wrap">
                    {Object.entries(currentTags).map(([typeSlug, values]) => {
                        const tagType = getTagTypeBySlug(typeSlug);
                        return values.map((value) => {
                            const tagValue = allSlugToValues[value];
                            const colors =
                                tagType ?
                                    resolveTagColors(theme, tagType, tagValue)
                                :   {
                                        background: theme.custom.grey[600],
                                        foreground: theme.custom.grey[100],
                                    };

                            return (
                                <RemovableTag
                                    key={`${typeSlug}-${value}`}
                                    label={value}
                                    backgroundColor={colors.background}
                                    foregroundColor={colors.foreground}
                                    removeLabel={
                                        disabled ? undefined : t("tags.actions.remove", "Remove {{value}}", { value })
                                    }
                                    onRemove={disabled ? undefined : () => handleTagRemove(typeSlug, value)}
                                />
                            );
                        });
                    })}
                </Stack>
            </Box>
        );
    }

    if (!isProductUxV2) {
        return (
            <Box>
                {hideTitle === false && (
                    <MuiTypography variant="h6" gutterBottom>
                        {t("tags.labels.tags", "Tags")}
                    </MuiTypography>
                )}

                {tagTypesLoading ?
                    <Skeleton variant="rectangular" width="100%" height={100} />
                :   <Box sx={{ mb: 3 }}>
                        {tagTypes.map((tagType: TagType) => {
                            const tagsOfType = getTagsByType(tagType.slug);

                            return (
                                <Box key={tagType.id} sx={{ mb: 2 }}>
                                    <MuiTypography variant="subtitle2" color="text.secondary" gutterBottom>
                                        {tagType.name}
                                        {tagType.required && <span style={{ color: "red" }}> *</span>}
                                        {!tagType.multiple && (
                                            <span style={{ fontSize: "0.8em" }}>
                                                {t("tags.labels.single", " (single)")}
                                            </span>
                                        )}
                                    </MuiTypography>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                        {tagsOfType.length > 0 ?
                                            tagsOfType.map((value) => {
                                                const tagValue = allSlugToValues[value];
                                                const colors = resolveLegacyTagColors(tagType, tagValue);

                                                return (
                                                    <Chip
                                                        key={`${tagType.slug}-${value}`}
                                                        label={tagValue?.name}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: colors.background,
                                                            color: colors.foreground,
                                                            fontWeight: "bold",
                                                            fontSize: "0.75rem",
                                                            "& .MuiChip-deleteIcon": {
                                                                color: colors.foreground,
                                                                "&:hover": {
                                                                    color: colors.foreground,
                                                                    opacity: 0.7,
                                                                },
                                                            },
                                                        }}
                                                        onDelete={
                                                            disabled ? undefined : (
                                                                () => handleTagRemove(tagType.slug, value)
                                                            )
                                                        }
                                                    />
                                                );
                                            })
                                        :   <MuiTypography variant="body2" color="text.disabled">
                                                {t("tags.labels.no_tags_selected", "No tags selected")}
                                            </MuiTypography>
                                        }
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Box>
                }

                {!disabled && (
                    <Box>
                        <FormControl size="small" sx={{ mb: 2 }}>
                            <MuiTypography variant="body-semibold" sx={{ paddingBottom: "0.5rem" }}>
                                {t("tags.labels.select_tag_type", "Select Tag Type:")}
                            </MuiTypography>
                            <FoodsSelectServerPagination
                                onChange={setSelectedTagType}
                                options={tagTypes}
                                selectedOption={selectedTagType}
                                getId={(item) => item?.id || ""}
                                getName={(item) => item?.name || ""}
                                textOptions={{
                                    placeholder: t("tags.placeholders.select_tag_type", "Select tag type..."),
                                    searchPlaceholder: t("tags.placeholders.search_tag_types", "Search tag types..."),
                                    emptyOptionsText: t(
                                        "tags.messages.no_tag_types_available",
                                        "No tag types available",
                                    ),
                                    defaultValueText: t(
                                        "tags.messages.default_value_text_tag_type",
                                        "Select a tag type",
                                    ),
                                    noResult: t("tags.messages.no_tag_types_found", "No tag types found"),
                                }}
                                search={tagTypeSearch}
                                setSearch={setTagTypeSearch}
                                isSearchFound={isTagTypeSearchFound}
                                BoxProps={{ width: "100%" }}
                                BoxOptionsProps={{ width: "500px" }}
                            />
                        </FormControl>

                        {selectedTagType && (
                            <Box mt={2}>
                                <FormControl size="small">
                                    <MuiTypography variant="h5" sx={{ paddingBottom: "0.5rem" }}>
                                        {t("tags.labels.select_tag_value", "Select Tag Value:")}
                                    </MuiTypography>
                                    {remainingTagValues.length === 0 ?
                                        <MuiTypography variant="small-bold">
                                            {t("tags.messages.no_tag_values_available", "No tag values available")}
                                        </MuiTypography>
                                    :   <FoodsSelectServerPagination
                                            onChange={(tagValue) => {
                                                if (tagValue) handleTagValueSelect(tagValue);
                                            }}
                                            options={remainingTagValues}
                                            selectedOption={null}
                                            getId={(item) => item?.id || ""}
                                            getName={(item) => item?.name || ""}
                                            textOptions={{
                                                placeholder: t(
                                                    "tags.placeholders.select_tag_value",
                                                    "Select tag value...",
                                                ),
                                                searchPlaceholder: t(
                                                    "tags.placeholders.search_tag_values",
                                                    "Search tag values...",
                                                ),
                                                emptyOptionsText: t(
                                                    "tags.messages.no_tag_values_available",
                                                    "No tag values available",
                                                ),
                                                defaultValueText: t(
                                                    "tags.messages.default_value_text_tag_value",
                                                    "Select a tag value",
                                                ),
                                                noResult: t("tags.messages.no_tag_values_found", "No tag values found"),
                                            }}
                                            search={tagValueSearch}
                                            setSearch={setTagValueSearch}
                                            isSearchFound={isTagValueSearchFound}
                                            BoxProps={{ width: "100%" }}
                                            BoxOptionsProps={{ width: "500px" }}
                                        />
                                    }
                                </FormControl>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box>
            {hideTitle === false && (
                <FoodsTypography variant="big-bold" sx={{ marginBottom: theme.spacing(2) }}>
                    {t("tags.labels.tags", "Tags")}
                </FoodsTypography>
            )}

            {/* Display current tags grouped by type */}
            {tagTypesLoading ?
                <FoodsFieldSkeleton label={t("tags.messages.loading", "Loading tags")} height={theme.spacing(12.5)} />
            :   <Box sx={{ marginBottom: theme.spacing(3) }}>
                    {tagTypes.map((tagType: TagType) => {
                        const tagsOfType = getTagsByType(tagType.slug);

                        return (
                            <Box key={tagType.id} sx={{ marginBottom: theme.spacing(2) }}>
                                <FoodsTypography
                                    variant="body-semibold"
                                    sx={{ color: theme.custom.grey[2000], marginBottom: theme.spacing(1) }}
                                >
                                    {tagType.name}
                                    {tagType.required && (
                                        <Box component="span" sx={{ color: theme.custom.red[600] }}>
                                            {" *"}
                                        </Box>
                                    )}
                                    {!tagType.multiple && (
                                        <>
                                            {" "}
                                            <FoodsTypography component="span" variant="small">
                                                {t("tags.labels.single", "(single)")}
                                            </FoodsTypography>
                                        </>
                                    )}
                                </FoodsTypography>

                                <Stack
                                    direction="row"
                                    gap={theme.spacing(1)}
                                    flexWrap="wrap"
                                    sx={{ marginBottom: theme.spacing(1) }}
                                >
                                    {tagsOfType.length > 0 ?
                                        tagsOfType.map((value) => {
                                            const tagValue = allSlugToValues[value];
                                            const colors = resolveTagColors(theme, tagType, tagValue);

                                            return (
                                                <RemovableTag
                                                    key={`${tagType.slug}-${value}`}
                                                    label={tagValue?.name ?? ""}
                                                    backgroundColor={colors.background}
                                                    foregroundColor={colors.foreground}
                                                    removeLabel={
                                                        disabled ? undefined : (
                                                            t("tags.actions.remove", "Remove {{value}}", {
                                                                value: tagValue?.name ?? value,
                                                            })
                                                        )
                                                    }
                                                    onRemove={
                                                        disabled ? undefined : (
                                                            () => handleTagRemove(tagType.slug, value)
                                                        )
                                                    }
                                                />
                                            );
                                        })
                                    :   <FoodsTypography variant="body-regular" sx={{ color: theme.custom.grey[1400] }}>
                                            {t("tags.labels.no_tags_selected", "No tags selected")}
                                        </FoodsTypography>
                                    }
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            }

            {/* Add new tags */}
            {!disabled && (
                <Stack sx={{ gap: theme.spacing(2) }}>
                    <FoodsServerSelectField
                        appearance="product-v2"
                        label={t("tags.labels.select_tag_type", "Select Tag Type:")}
                        onChange={setSelectedTagType}
                        options={tagTypes}
                        selectedOption={selectedTagType}
                        getId={(item) => item?.id || ""}
                        getName={(item) => item?.name || ""}
                        placeholder={t("tags.placeholders.select_tag_type", "Select tag type...")}
                        searchPlaceholder={t("tags.placeholders.search_tag_types", "Search tag types...")}
                        emptyOptionsText={t("tags.messages.no_tag_types_available", "No tag types available")}
                        defaultValueText={t("tags.messages.default_value_text_tag_type", "Select a tag type")}
                        noResultText={t("tags.messages.no_tag_types_found", "No tag types found")}
                        search={tagTypeSearch}
                        setSearch={setTagTypeSearch}
                        isSearchFound={isTagTypeSearchFound}
                        boxOptionsProps={{ width: theme.spacing(62.5) }}
                    />

                    {selectedTagType &&
                        (remainingTagValues.length === 0 ?
                            <FoodsTypography variant="small-bold">
                                {t("tags.messages.no_tag_values_available", "No tag values available")}
                            </FoodsTypography>
                        :   <FoodsServerSelectField
                                appearance="product-v2"
                                label={t("tags.labels.select_tag_value", "Select Tag Value:")}
                                onChange={(tagValue) => {
                                    if (tagValue) handleTagValueSelect(tagValue);
                                }}
                                options={remainingTagValues}
                                selectedOption={null}
                                getId={(item) => item?.id || ""}
                                getName={(item) => item?.name || ""}
                                placeholder={t("tags.placeholders.select_tag_value", "Select tag value...")}
                                searchPlaceholder={t("tags.placeholders.search_tag_values", "Search tag values...")}
                                emptyOptionsText={t("tags.messages.no_tag_values_available", "No tag values available")}
                                defaultValueText={t("tags.messages.default_value_text_tag_value", "Select a tag value")}
                                noResultText={t("tags.messages.no_tag_values_found", "No tag values found")}
                                search={tagValueSearch}
                                setSearch={setTagValueSearch}
                                isSearchFound={isTagValueSearchFound}
                                boxOptionsProps={{ width: theme.spacing(62.5) }}
                            />)}
                </Stack>
            )}
        </Box>
    );
};

export default JsonTagSelector;
